'use client'

import * as React from 'react'
import { useDrag, useDrop, useDragLayer } from 'react-dnd'
import { cn } from '../../../../lib/utils'
import { Icon, type iconTypes } from '../../Icon'
import {
  NAV_DRAG_TYPES,
  NAV_GROUP_DRAG_TYPE,
  NAV_LINK_DRAG_TYPE,
  NAV_SECTION_DRAG_TYPE,
  canCreateNestUnderLink,
  canMoveNavigationItem,
  findEditorItemById,
  getExistingNestGroupIdForLinkAnchor,
  shouldCreateNewNestGroupUnderLink,
  type NavigationEditorItem,
  type NavigationLayoutChange,
} from '../../RestructurableNavigationLayout'

export type SidebarNavigationMeta = {
  id: string
  parentId: string | null
  kind: 'group' | 'section' | 'link'
  draggable?: boolean
  label?: string
  icon?: string
}

export type SidebarNavDragItem = {
  id: string
  label: string
  icon?: string
  kind: 'group' | 'section' | 'link'
}

export type PendingNestPreview = {
  anchorId: string
  draggedItem: SidebarNavDragItem
  mode: 'create' | 'existing'
}

const NEST_HOVER_MS = 500

type SidebarNavigationDragContextValue = {
  enabled: boolean
  isDragActive: boolean
  navTree: NavigationEditorItem[]
  onMove: (dragId: string, parentId: string | null, index: number) => void
  onNestCreate?: (anchorId: string, dragId: string) => void
  dragExpandedIds: ReadonlySet<string>
  nestReadyIds: ReadonlySet<string>
  pendingNestPreview: PendingNestPreview | null
  setNestReady: (id: string, ready: boolean) => void
  setDragExpanded: (id: string, expanded: boolean) => void
  setPendingNestPreview: (preview: PendingNestPreview | null) => void
}

const SidebarNavigationDragContext =
  React.createContext<SidebarNavigationDragContextValue>({
    enabled: false,
    isDragActive: false,
    navTree: [],
    onMove: () => undefined,
    dragExpandedIds: new Set(),
    nestReadyIds: new Set(),
    pendingNestPreview: null,
    setNestReady: () => undefined,
    setDragExpanded: () => undefined,
    setPendingNestPreview: () => undefined,
  })

export function SidebarNavigationDragProvider({
  enabled,
  navTree,
  onMove,
  onNestCreate,
  children,
}: {
  enabled: boolean
  navTree: NavigationEditorItem[]
  onMove: (dragId: string, parentId: string | null, index: number) => void
  onNestCreate?: (anchorId: string, dragId: string) => void
  children: React.ReactNode
}) {
  const isDragActive = useDragLayer((monitor) => monitor.isDragging())
  const [dragExpandedIds, setDragExpandedIdsState] = React.useState<
    Set<string>
  >(() => new Set())
  const [nestReadyIds, setNestReadyIdsState] = React.useState<Set<string>>(
    () => new Set(),
  )
  const [pendingNestPreview, setPendingNestPreviewState] =
    React.useState<PendingNestPreview | null>(null)

  const setNestReady = React.useCallback((id: string, ready: boolean) => {
    setNestReadyIdsState((current) => {
      const next = new Set(current)
      if (ready) {
        next.add(id)
      } else {
        next.delete(id)
      }
      return next
    })
  }, [])

  const setDragExpanded = React.useCallback((id: string, expanded: boolean) => {
    setDragExpandedIdsState((current) => {
      const next = new Set(current)
      if (expanded) {
        next.add(id)
      } else {
        next.delete(id)
      }
      return next
    })
  }, [])

  const setPendingNestPreview = React.useCallback(
    (preview: PendingNestPreview | null) => {
      setPendingNestPreviewState(preview)
    },
    [],
  )

  React.useEffect(() => {
    if (!enabled || !isDragActive) {
      setDragExpandedIdsState(new Set())
      setNestReadyIdsState(new Set())
      setPendingNestPreviewState(null)
    }
  }, [enabled, isDragActive])

  const value = React.useMemo(
    () => ({
      enabled,
      isDragActive: enabled && isDragActive,
      navTree,
      onMove,
      onNestCreate,
      dragExpandedIds,
      nestReadyIds,
      pendingNestPreview,
      setNestReady,
      setDragExpanded,
      setPendingNestPreview,
    }),
    [
      enabled,
      isDragActive,
      navTree,
      onMove,
      onNestCreate,
      dragExpandedIds,
      nestReadyIds,
      pendingNestPreview,
      setNestReady,
      setDragExpanded,
      setPendingNestPreview,
    ],
  )

  return (
    <SidebarNavigationDragContext.Provider value={value}>
      {children}
    </SidebarNavigationDragContext.Provider>
  )
}

export function useSidebarNavigationDrag() {
  return React.useContext(SidebarNavigationDragContext)
}

export function SidebarNavDropZone({
  parentId,
  targetIndex,
  overlay = false,
  overlayPosition = 'top',
}: {
  parentId: string | null
  targetIndex: number
  overlay?: boolean
  overlayPosition?: 'top' | 'bottom'
}) {
  const { enabled, isDragActive, navTree, onMove } = useSidebarNavigationDrag()

  const [{ isOver, canDropHere }, drop] = useDrop({
    accept: NAV_DRAG_TYPES,
    canDrop: (item: { id: string }) => {
      if (!enabled || !isDragActive) return false
      return canMoveNavigationItem(navTree, item.id, parentId)
    },
    drop: (item: { id: string }) => {
      onMove(item.id, parentId, targetIndex)
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
      canDropHere: monitor.canDrop(),
    }),
  })

  if (!enabled) return null

  return (
    <div
      className={cn(
        'h-0 w-full shrink-0',
        overlay
          ? cn(
              'pointer-events-none absolute inset-x-0 z-20',
              overlayPosition === 'top'
                ? 'top-0 -translate-y-1/2'
                : 'bottom-0 translate-y-1/2',
            )
          : 'relative',
      )}
    >
      <div
        ref={drop as unknown as React.Ref<HTMLDivElement>}
        className={cn(
          'absolute left-1 right-1 top-0 h-4 -translate-y-1/2',
          !isDragActive && 'pointer-events-none',
        )}
      >
        {isDragActive && isOver && canDropHere ? (
          <div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-primary/50" />
        ) : null}
      </div>
    </div>
  )
}

export function useSidebarNavNestDropTarget({
  navigationId,
  isOpen = false,
  onExpand,
  previewMode = 'existing',
}: {
  navigationId: string | null
  isOpen?: boolean
  onExpand?: () => void
  previewMode?: PendingNestPreview['mode']
}) {
  const {
    enabled,
    isDragActive,
    navTree,
    onMove,
    setNestReady,
    setDragExpanded,
    setPendingNestPreview,
    nestReadyIds,
  } = useSidebarNavigationDrag()

  const dwellTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  const clearDwell = React.useCallback(() => {
    if (dwellTimeoutRef.current) {
      clearTimeout(dwellTimeoutRef.current)
      dwellTimeoutRef.current = null
    }
  }, [])

  const nestReady = navigationId ? nestReadyIds.has(navigationId) : false

  const getNestIndex = React.useCallback(() => {
    if (!navigationId) return 0
    const parent = findEditorItemById(navTree, navigationId)
    return parent?.children?.length ?? 0
  }, [navTree, navigationId])

  const [{ isOver, canDropHere, isHovering }, drop] = useDrop({
    accept: NAV_DRAG_TYPES,
    canDrop: (item: SidebarNavDragItem) => {
      if (!enabled || !isDragActive || !navigationId) return false
      if (item.id === navigationId) return false
      return canMoveNavigationItem(navTree, item.id, navigationId)
    },
    drop: (item: SidebarNavDragItem) => {
      if (!navigationId) return
      onMove(item.id, navigationId, getNestIndex())
    },
    hover: (item: SidebarNavDragItem, monitor) => {
      if (!enabled || !isDragActive || !navigationId) return
      if (item.id === navigationId) return
      if (!canMoveNavigationItem(navTree, item.id, navigationId)) return

      if (monitor.isOver({ shallow: true })) {
        if (!dwellTimeoutRef.current && !nestReadyIds.has(navigationId)) {
          dwellTimeoutRef.current = setTimeout(() => {
            dwellTimeoutRef.current = null
            setNestReady(navigationId, true)
            setPendingNestPreview({
              anchorId: navigationId,
              draggedItem: item,
              mode: previewMode,
            })
            if (!isOpen) {
              setDragExpanded(navigationId, true)
              onExpand?.()
            }
          }, NEST_HOVER_MS)
        }
        return
      }

      clearDwell()
      setNestReady(navigationId, false)
      setPendingNestPreview(null)
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
      canDropHere: monitor.canDrop(),
      isHovering: monitor.isOver({ shallow: true }),
    }),
  })

  React.useEffect(() => clearDwell, [clearDwell])

  React.useEffect(() => {
    if (!isDragActive) {
      clearDwell()
    }
  }, [isDragActive, clearDwell])

  React.useEffect(() => {
    if (!isHovering && navigationId) {
      clearDwell()
      setNestReady(navigationId, false)
      setPendingNestPreview(null)
    }
  }, [
    isHovering,
    navigationId,
    clearDwell,
    setNestReady,
    setPendingNestPreview,
  ])

  const nestDropRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      if (!enabled || !navigationId) return
      drop(node)
    },
    [drop, enabled, navigationId],
  )

  return {
    nestDropRef: enabled && navigationId ? nestDropRef : undefined,
    isNestHover: isDragActive && isHovering,
    isNestReady: isDragActive && nestReady,
    canNestDrop: isDragActive && nestReady && isOver && canDropHere,
    nestTargetClassName: cn({
      'bg-accent/30 ring-1 ring-primary/30':
        isDragActive && isHovering && !nestReady,
      'bg-primary/10 ring-2 ring-primary/50':
        isDragActive && nestReady && isOver,
    }),
  }
}

export function useSidebarNavCreateNestTarget(
  navigation: SidebarNavigationMeta | undefined,
) {
  const {
    enabled,
    isDragActive,
    navTree,
    onMove,
    onNestCreate,
    setNestReady,
    setPendingNestPreview,
    nestReadyIds,
  } = useSidebarNavigationDrag()

  const navigationId = navigation?.id ?? null
  const dwellTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  const clearDwell = React.useCallback(() => {
    if (dwellTimeoutRef.current) {
      clearTimeout(dwellTimeoutRef.current)
      dwellTimeoutRef.current = null
    }
  }, [])

  const nestReady = navigationId ? nestReadyIds.has(navigationId) : false

  const canNestUnderAnchor = React.useCallback(
    (dragId: string) => {
      if (!navigationId) return false
      if (!canCreateNestUnderLink(navTree, navigationId, dragId)) {
        return false
      }

      const existingGroupId = getExistingNestGroupIdForLinkAnchor(
        navTree,
        navigationId,
      )
      if (existingGroupId) {
        return canMoveNavigationItem(navTree, dragId, existingGroupId)
      }

      return shouldCreateNewNestGroupUnderLink(navTree, navigationId, dragId)
    },
    [navTree, navigationId],
  )

  const handleNestDrop = React.useCallback(
    (dragId: string) => {
      if (!navigationId) return

      const existingGroupId = getExistingNestGroupIdForLinkAnchor(
        navTree,
        navigationId,
      )
      if (existingGroupId) {
        const parent = findEditorItemById(navTree, existingGroupId)
        onMove(dragId, existingGroupId, parent?.children?.length ?? 0)
        return
      }

      onNestCreate?.(navigationId, dragId)
    },
    [navTree, navigationId, onMove, onNestCreate],
  )

  const [{ isOver, canDropHere, isHovering }, drop] = useDrop({
    accept: NAV_DRAG_TYPES,
    canDrop: (item: SidebarNavDragItem) => {
      if (!enabled || !isDragActive || !navigationId) {
        return false
      }
      if (
        getExistingNestGroupIdForLinkAnchor(navTree, navigationId) == null &&
        !onNestCreate
      ) {
        return false
      }
      return canNestUnderAnchor(item.id)
    },
    drop: (item: SidebarNavDragItem) => {
      handleNestDrop(item.id)
    },
    hover: (item: SidebarNavDragItem, monitor) => {
      if (!enabled || !isDragActive || !navigationId) return
      if (!canNestUnderAnchor(item.id)) return

      if (monitor.isOver({ shallow: true })) {
        if (!dwellTimeoutRef.current && !nestReadyIds.has(navigationId)) {
          dwellTimeoutRef.current = setTimeout(() => {
            dwellTimeoutRef.current = null
            setNestReady(navigationId, true)
            setPendingNestPreview({
              anchorId: navigationId,
              draggedItem: item,
              mode: getExistingNestGroupIdForLinkAnchor(navTree, navigationId)
                ? 'existing'
                : 'create',
            })
          }, NEST_HOVER_MS)
        }
        return
      }

      clearDwell()
      setNestReady(navigationId, false)
      setPendingNestPreview(null)
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
      canDropHere: monitor.canDrop(),
      isHovering: monitor.isOver({ shallow: true }),
    }),
  })

  React.useEffect(() => clearDwell, [clearDwell])

  React.useEffect(() => {
    if (!isDragActive) {
      clearDwell()
    }
  }, [isDragActive, clearDwell])

  React.useEffect(() => {
    if (!isHovering && navigationId) {
      clearDwell()
      setNestReady(navigationId, false)
      setPendingNestPreview(null)
    }
  }, [
    isHovering,
    navigationId,
    clearDwell,
    setNestReady,
    setPendingNestPreview,
  ])

  const createNestDropRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      if (!enabled || !navigationId) return
      drop(node)
    },
    [drop, enabled, navigationId],
  )

  return {
    createNestDropRef:
      enabled && navigationId && onNestCreate ? createNestDropRef : undefined,
    isNestHover: isDragActive && isHovering,
    isNestReady: isDragActive && nestReady,
    canNestDrop: isDragActive && nestReady && isOver && canDropHere,
    nestTargetClassName: cn({
      'bg-accent/30 ring-1 ring-primary/30':
        isDragActive && isHovering && !nestReady,
      'bg-primary/10 ring-2 ring-primary/50':
        isDragActive && nestReady && isOver,
    }),
  }
}

export function SidebarNavNestGhostPreview({
  anchorId,
  nested = false,
}: {
  anchorId: string
  nested?: boolean
}) {
  const { isDragActive, pendingNestPreview, nestReadyIds } =
    useSidebarNavigationDrag()

  if (
    !isDragActive ||
    pendingNestPreview?.anchorId !== anchorId ||
    !nestReadyIds.has(anchorId)
  ) {
    return null
  }

  const { draggedItem } = pendingNestPreview

  return (
    <div className="overflow-hidden pb-1">
      <ul
        className={cn(
          'border-sidebar-border flex min-w-0 flex-col border-l py-0.5',
          'mx-0 ml-2 mr-0 border-0 px-0 py-0.5',
        )}
      >
        <li className="group/menu-sub-item relative">
          <div
            className={cn(
              'pointer-events-none flex h-7 min-h-7 items-center gap-2 rounded-md py-1 pl-3 pr-2 opacity-40',
            )}
          >
            <span className="flex size-4 shrink-0 items-center justify-center [&_svg]:size-4">
              <Icon
                name={(draggedItem.icon as iconTypes) || 'Text'}
                className="text-muted-foreground/90"
              />
            </span>
            <span className="truncate text-sm text-muted-foreground/90">
              {draggedItem.label}
            </span>
          </div>
        </li>
      </ul>
    </div>
  )
}

export function useSidebarNavItemDrag(
  navigation: SidebarNavigationMeta | undefined,
  displayLabel?: string,
) {
  const { enabled } = useSidebarNavigationDrag()

  const dragType =
    navigation?.kind === 'link'
      ? NAV_LINK_DRAG_TYPE
      : navigation?.kind === 'section'
        ? NAV_SECTION_DRAG_TYPE
        : NAV_GROUP_DRAG_TYPE

  const canDrag =
    enabled && navigation != null && navigation.draggable !== false

  const [{ isDragging }, drag, preview] = useDrag({
    type: dragType,
    item: (): SidebarNavDragItem => ({
      id: navigation?.id ?? '',
      label:
        displayLabel ??
        navigation?.label ??
        (navigation?.kind === 'section'
          ? 'Section'
          : navigation?.kind === 'group'
            ? 'Group'
            : 'Page'),
      icon: navigation?.icon,
      kind: navigation?.kind ?? 'link',
    }),
    canDrag: () => canDrag,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  const dragRef = React.useCallback(
    (node: HTMLElement | null) => {
      if (!canDrag) return
      drag(node)
      preview(node)
    },
    [canDrag, drag, preview],
  )

  return {
    isDragging: canDrag && isDragging,
    dragRef: canDrag ? dragRef : undefined,
    canDrag,
  }
}

export function sidebarNavDragClassName(isDragging?: boolean) {
  return cn({
    'opacity-40': isDragging,
  })
}

export type { NavigationLayoutChange }
